import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import {
    OrderDetailResponse,
    SelectionPart,
    SelectionStatus,
    SelectionTab,
} from './useGetOrderDetail';

interface MarkPartOrderedInput {
    orderId: string;
    orderPartId: string;
}

const markPartOrdered = async (data: MarkPartOrderedInput, sessionId: string | null) => {
    return axios.patch(`${API_URL}/order/part/ordered`, data, {
        headers: {
            Authorization: sessionId,
        },
    });
};

const isSelectedTab = (tab: SelectionTab): boolean =>
    tab.key === 'ordered' || tab.title.trim().toLowerCase() === 'selected';

const isPendingTab = (tab: SelectionTab): boolean =>
    tab.key === 'pending' || tab.title.trim().toLowerCase() === 'pending';

const moveMarkedPart = (selection: Array<SelectionTab>, orderPartId: string): Array<SelectionTab> => {
    let movedPart: SelectionPart | null = null;
    let sourceTabKey: SelectionTab['key'] | null = null;

    for (const tab of selection) {
        if (tab.key === 'all') continue;
        const found = tab.parts.find((part) => part.partId === orderPartId);
        if (found) {
            movedPart = found;
            sourceTabKey = tab.key;
            break;
        }
    }

    if (!movedPart) {
        movedPart = selection.find((tab) => tab.key === 'all')?.parts.find((part) => part.partId === orderPartId) ?? null;
    }
    if (!movedPart) return selection;

    const sourceTab = sourceTabKey ? selection.find((tab) => tab.key === sourceTabKey) : undefined;
    const leavingSelected = !!sourceTab && isSelectedTab(sourceTab);
    const updatedPart: SelectionPart = leavingSelected
        ? {
            ...movedPart,
            status: 'selection',
            selectionStatus: (movedPart.selectedOptionId ? 'manual' : 'partbay') as SelectionStatus,
        }
        : {
            ...movedPart,
            status: 'confirmed',
            selectionStatus: 'ordered',
        };

    const existingTarget = selection.find((tab) => (leavingSelected ? isPendingTab(tab) : isSelectedTab(tab)));
    const targetKey = existingTarget?.key ?? (leavingSelected ? 'pending' : 'ordered');

    let next = selection.map((tab) => {
        if (tab.key === 'all') {
            return {
                ...tab,
                parts: tab.parts.map((part) => (part.partId === orderPartId ? updatedPart : part)),
            };
        }
        if (tab.key === sourceTabKey && tab.key !== targetKey) {
            const parts = tab.parts.filter((part) => part.partId !== orderPartId);
            return { ...tab, parts, total: parts.length };
        }
        if (tab.key === targetKey) {
            const parts = [...tab.parts.filter((part) => part.partId !== orderPartId), updatedPart];
            return { ...tab, parts, total: parts.length };
        }
        return tab;
    });

    if (!next.some((tab) => tab.key === targetKey)) {
        next = [
            ...next,
            {
                key: targetKey,
                title: leavingSelected ? 'Pending' : 'Selected',
                total: 1,
                parts: [updatedPart],
            },
        ];
    }

    return next.filter((tab) => tab.key === 'all' || tab.parts.length > 0);
};

export const useMarkPartOrdered = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: MarkPartOrderedInput) => markPartOrdered(data, sessionContext?.sessionId ?? null),
        onMutate: async ({ orderId, orderPartId }) => {
            await queryClient.cancelQueries({ queryKey: ['/order/detail', orderId] });
            const previousOrder = queryClient.getQueryData<OrderDetailResponse>(['/order/detail', orderId]);

            queryClient.setQueryData<OrderDetailResponse>(['/order/detail', orderId], (old) => {
                if (!old?.selection) return old;
                return {
                    ...old,
                    selection: moveMarkedPart(old.selection, orderPartId),
                };
            });

            return { previousOrder };
        },
        onError: (_error, { orderId }, context) => {
            if (context?.previousOrder) {
                queryClient.setQueryData(['/order/detail', orderId], context.previousOrder);
            }
        },
        onSuccess: (_data, { orderId }) => {
            queryClient.invalidateQueries({ queryKey: ['/order/detail', orderId] });
        },
    });
};
